import React, { useState, useEffect, useCallback } from 'react';
import { getLeads } from '../api';
import CustomerDetail from './CustomerDetail';
import DashboardNav from '../components/dashboard/DashboardNav';
import { useNavigate } from 'react-router-dom';

function LeadsOverview({ user }) {
  const [leads, setLeads] = useState([]);
  const [allLeads, setAllLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth <= 1024 && window.innerWidth > 768);

  const navigate = useNavigate();

  // Filter State - Produkt-Filter hinzugefügt, Status-Filter wieder da mit localStorage-Persistenz
  const [filters, setFilters] = useState(() => {
    // Filter aus localStorage laden oder Standardwerte verwenden
    const savedFilters = localStorage.getItem('leadsOverviewFilters');
    if (savedFilters) {
      try {
        return JSON.parse(savedFilters);
      } catch (error) {
        console.error('Fehler beim Laden der gespeicherten LeadsOverview-Filter:', error);
      }
    }
    // Standardwerte
    return {
      sortBy: 'erstellt', // erstellt, name
      sortOrder: 'desc', // desc, asc
      dateFrom: '',
      dateTo: '',
      produkt: '', // Filter nach Produkt (PV/WP)
      status: '', // Kombinierter Status-Filter für beide Produkttypen
    };
  });

  // Mobile Responsive Check
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsTablet(window.innerWidth <= 1024 && window.innerWidth > 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Navigation Handler
  const handleLogout = () => {
    localStorage.removeItem('user');
    navigate('/');
  };

  const handleSectionChange = (section) => {
    if (section === 'dashboard') {
      navigate('/dashboard');
    } else if (section === 'termine') {
      navigate('/termine');
    } else if (section === 'stellungnahmen') {
      navigate('/stellungnahmen');
    } else if (section === 'leads') {
      navigate('/dashboard');
    }
  };

  // Responsive Styles - gleich wie in Leads.js
  const styles = {
    container: {
      margin: '0 auto',
      maxWidth: '1400px',
      padding: isMobile ? '5px' : '15px',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: isMobile ? 'flex-start' : 'center',
      marginBottom: '15px',
      flexDirection: isMobile ? 'column' : 'row',
      gap: isMobile ? '10px' : '0',
    },
    title: {
      color: '#2d3748',
      fontSize: isMobile ? '1.2rem' : '1.6rem',
      margin: 0,
    },
    filters: {
      background: 'white',
      borderRadius: '6px',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      padding: isMobile ? '10px' : '15px',
      marginBottom: '15px',
    },
    filterRow: {
      display: 'flex',
      gap: isMobile ? '8px' : '15px',
      marginBottom: '12px',
      flexWrap: 'wrap',
      flexDirection: isMobile ? 'column' : 'row',
    },
    filterGroup: {
      display: 'flex',
      flexDirection: 'column',
      minWidth: isMobile ? 'auto' : '120px',
      flex: isMobile ? '1' : 'none',
    },
    filterLabel: {
      fontSize: '0.8rem',
      fontWeight: '500',
      color: '#4a5568',
      marginBottom: '4px',
    },
    filterInput: {
      padding: '6px 8px',
      border: '1px solid #e2e8f0',
      borderRadius: '4px',
      fontSize: '0.8rem',
    },
    resetButton: {
      padding: '6px 12px',
      backgroundColor: '#f3f4f6',
      color: '#4b5563',
      border: '1px solid #d1d5db',
      borderRadius: '4px',
      fontSize: '0.8rem',
      cursor: 'pointer',
      marginTop: isMobile ? '8px' : '15px',
      width: isMobile ? '100%' : 'auto',
    },
    tableContainer: {
      background: 'white',
      borderRadius: '6px',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      overflow: 'auto',
      WebkitOverflowScrolling: 'touch',
      maxHeight: '70vh',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      minWidth: isMobile ? '800px' : isTablet ? '900px' : 'auto', // Gleiche Mindestbreite wie Leads.js
      fontSize: isMobile ? '0.7rem' : isTablet ? '0.75rem' : '0.85rem',
    },
    th: {
      background: '#f7fafc',
      padding: isMobile ? '6px 4px' : isTablet ? '8px 6px' : '10px 8px',
      textAlign: 'left',
      fontWeight: '600',
      color: '#4a5568',
      borderBottom: '1px solid #e2e8f0',
      fontSize: isMobile ? '0.65rem' : isTablet ? '0.7rem' : '0.75rem',
      whiteSpace: 'nowrap',
      position: 'sticky',
      top: 0,
      zIndex: 10,
    },
    td: {
      padding: isMobile ? '6px 4px' : isTablet ? '8px 6px' : '10px 8px',
      borderBottom: '1px solid #e2e8f0',
      fontSize: isMobile ? '0.65rem' : isTablet ? '0.7rem' : '0.75rem',
      maxWidth: isMobile ? '80px' : isTablet ? '100px' : '150px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    // Specific column widths
    dateColumn: {
      minWidth: isMobile ? '70px' : '80px',
      maxWidth: isMobile ? '70px' : '80px',
    },
    productColumn: {
      minWidth: isMobile ? '50px' : '60px',
      maxWidth: isMobile ? '50px' : '60px',
    },
    statusColumn: {
      minWidth: isMobile ? '80px' : '100px',
      maxWidth: isMobile ? '100px' : '120px',
    },
    nameColumn: {
      minWidth: isMobile ? '100px' : '120px',
      maxWidth: isMobile ? '150px' : '200px',
    },
    row: {
      cursor: 'pointer',
      transition: 'background-color 0.2s',
    },
    statusBadge: {
      display: 'inline-block',
      padding: isMobile ? '0.15rem 0.3rem' : '0.2rem 0.5rem',
      borderRadius: '0.25rem',
      fontSize: isMobile ? '0.55rem' : '0.65rem',
      fontWeight: '500',
      textAlign: 'center',
      whiteSpace: 'nowrap',
      minWidth: isMobile ? '35px' : '50px',
      maxWidth: isMobile ? '80px' : '100px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    productBadges: {
      display: 'flex',
      gap: '0.15rem',
      flexWrap: 'wrap',
    },
    productBadge: {
      display: 'inline-block',
      padding: isMobile ? '0.1rem 0.25rem' : '0.15rem 0.4rem',
      borderRadius: '0.2rem',
      fontSize: isMobile ? '0.55rem' : '0.6rem',
      fontWeight: '700',
    },
    pvBadge: {
      backgroundColor: '#c3dafe',
      color: '#4a6de5',
    },
    wpBadge: {
      backgroundColor: '#b2f5ea',
      color: '#3182ce',
    },
    loading: {
      textAlign: 'center',
      padding: '20px',
      color: '#718096',
      fontSize: '1rem',
    },
    error: {
      color: '#e53e3e',
      backgroundColor: '#fed7d7',
      padding: '8px',
      borderRadius: '4px',
      marginBottom: '12px',
      textAlign: 'center',
      fontSize: '0.85rem',
    },
    noData: {
      textAlign: 'center',
      padding: '20px',
      color: '#a0aec0',
      fontSize: '0.9rem',
    },
    mobileScrollHint: {
      display: isMobile || isTablet ? 'block' : 'none',
      fontSize: '0.65rem',
      color: '#718096',
      textAlign: 'center',
      marginTop: '8px',
      fontStyle: 'italic',
      padding: '5px',
    },
  };

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    setError('');
    
    try {
      const data = await getLeads(user.id);
      
      if (data.success) {
        setAllLeads(data.leads);
      } else {
        setError(data.error || 'Fehler beim Abrufen der Leads');
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [user.id]);

  // Filter anwenden - angepasst für Produktfilter
  const applyFilters = useCallback(() => {
    let filtered = [];
    
    // Leads in separate Einträge für PV und WP aufteilen
    allLeads.forEach(lead => {
      if (lead.produkt?.toLowerCase().includes('pv')) {
        filtered.push({
          ...lead,
          leadType: 'PV',
          status: lead.status_pv,
          leadId: `${lead.id}-pv`
        });
      }
      
      if (lead.produkt?.toLowerCase().includes('wp')) {
        filtered.push({
          ...lead,
          leadType: 'WP', 
          status: lead.status_wp,
          leadId: `${lead.id}-wp`
        });
      }
    });
    
    // Datum-Filter
    if (filters.dateFrom) {
      filtered = filtered.filter(lead => 
        new Date(lead.erstellt) >= new Date(filters.dateFrom)
      );
    }
    
    if (filters.dateTo) {
      filtered = filtered.filter(lead => 
        new Date(lead.erstellt) <= new Date(filters.dateTo + 'T23:59:59')
      );
    }
    
    // Produkt-Filter (PV oder WP)
    if (filters.produkt) {
      filtered = filtered.filter(lead => 
        lead.leadType === filters.produkt
      );
    }
    
    // Status-Filter
    if (filters.status) {
      filtered = filtered.filter(lead => 
        lead.status === filters.status
      );
    }
    
    // Sortierung
    filtered.sort((a, b) => {
      let compareValue = 0;
      
      switch (filters.sortBy) {
        case 'name':
          compareValue = (a.name || '').localeCompare(b.name || '');
          break;
        case 'erstellt':
        default:
          compareValue = new Date(a.erstellt) - new Date(b.erstellt);
          break;
      }
      
      return filters.sortOrder === 'desc' ? -compareValue : compareValue;
    });
    
    setLeads(filtered);
  }, [allLeads, filters]);

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  // Filter zurücksetzen - Status hinzugefügt
  const resetFilters = () => {
    const defaultFilters = {
      sortBy: 'erstellt',
      sortOrder: 'desc',
      dateFrom: '',
      dateTo: '',
      produkt: '',
      status: '',
    };
    setFilters(defaultFilters);
    // Auch aus localStorage entfernen
    localStorage.removeItem('leadsOverviewFilters');
  };

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  useEffect(() => {
    applyFilters();
  }, [allLeads, filters, applyFilters]);

  // Filter im localStorage speichern, wenn sie sich ändern
  useEffect(() => {
    localStorage.setItem('leadsOverviewFilters', JSON.stringify(filters));
  }, [filters]);

  const getStatusColor = (status) => {
    if (!status) return "";
    
    status = status.toLowerCase();
    if (status.includes('offen') || status.includes('neu')) return "status-new";
    if (status.includes('bearbeitung') || status.includes('in arbeit')) return "status-in-progress";
    if (status.includes('abgeschlossen') || status.includes('fertig')) return "status-completed";
    if (status.includes('qualifiziert') || status.includes('bestätigt')) return "status-completed";
    if (status.includes('abgelehnt') || status.includes('storniert') || status.includes('kein interesse') || status.includes('widerruf') || status.includes('ungültig') || status.includes('test')) return "status-cancelled";
    return "";
  };

  // Status-Farben-Funktion - gleiche Farben wie in Leads.js
  const getStatusStyles = (status) => {
    if (!status) return { backgroundColor: '#ebf8ff', color: '#3182ce' };
    
    status = status.toLowerCase();
    if (status.includes('offen') || status.includes('neu')) 
      return { backgroundColor: '#ebf8ff', color: '#3182ce' };
    if (status.includes('bearbeitung') || status.includes('in arbeit')) 
      return { backgroundColor: '#fefcbf', color: '#d69e2e' };
    if (status.includes('abgeschlossen') || status.includes('fertig') || status.includes('qualifiziert')) 
      return { backgroundColor: '#c6f6d5', color: '#38a169' };
    if (status.includes('abgelehnt') || status.includes('storniert') || status.includes('kein interesse') || status.includes('widerruf') || status.includes('ungültig') || status.includes('test')) 
      return { backgroundColor: '#fed7d7', color: '#e53e3e' };
    return { backgroundColor: '#ebf8ff', color: '#3182ce' };
  };

  const handleCustomerSelect = (customerId, e) => {
    e.stopPropagation();
    setSelectedCustomerId(customerId);
  };

  const handleBackToList = () => {
    setSelectedCustomerId(null);
  };

  // Truncate text helper - gleich wie in Leads.js
  const truncateText = (text, maxLength) => {
    if (!text) return '-';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '..';
  };

  if (selectedCustomerId) {
    return (
      <CustomerDetail 
        user={user} 
        customerId={selectedCustomerId} 
        onBack={handleBackToList} 
      />
    );
  }

  return (
    <>
      <DashboardNav 
        activeSection="leads-overview"
        onSectionChange={handleSectionChange}
        onLogout={handleLogout}
        user={user}
        onOpenOrganizationManagement={() => {}}
        onOpenMembersManagement={() => {}}
        onViewOrganizationLeads={() => {}}
        onViewTeamLeads={() => {}}
        showOrgManagement={false}
        showMembersManagement={false}
        showOrganizationLeads={false}
      />
      <div style={styles.container}>
        <div style={styles.header}>
          <h2 style={styles.title}>
            Leads
            <span style={{ 
              fontSize: '0.7em', 
              color: '#4a6de5', 
              marginLeft: '8px',
              fontWeight: 'normal'
            }}>
              ({leads.length})
            </span>
          </h2>
        </div>

        {/* Filter-Bereich */}
        <div style={styles.filters}>
          <div style={styles.filterRow}>
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Sortieren nach:</label>
              <select 
                style={styles.filterInput}
                value={filters.sortBy} 
                onChange={(e) => handleFilterChange('sortBy', e.target.value)}
              >
                <option value="erstellt">Erstellungsdatum</option>
                <option value="name">Name</option>
              </select>
            </div>
            
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Reihenfolge:</label>
              <select 
                style={styles.filterInput}
                value={filters.sortOrder} 
                onChange={(e) => handleFilterChange('sortOrder', e.target.value)}
              >
                <option value="desc">↓ Neu-Alt</option>
                <option value="asc">↑ Alt-Neu</option>
              </select>
            </div>
          </div>
          
          <div style={styles.filterRow}>
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Von Datum:</label>
              <input 
                style={styles.filterInput}
                type="date" 
                value={filters.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
              />
            </div>
            
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Bis Datum:</label>
              <input 
                style={styles.filterInput}
                type="date" 
                value={filters.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
              />
            </div>
            
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Produkt:</label>
              <select 
                style={styles.filterInput}
                value={filters.produkt} 
                onChange={(e) => handleFilterChange('produkt', e.target.value)}
              >
                <option value="">Alle Produkte</option>
                <option value="PV">PV</option>
                <option value="WP">WP</option>
              </select>
            </div>
            
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Status:</label>
              <select 
                style={styles.filterInput}
                value={filters.status} 
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="">Alle Status</option>
                <option value="Offen">Offen</option>
                <option value="Qualifiziert">Qualifiziert</option>
                <option value="Kein Interesse mehr">Kein Interesse mehr</option>
                <option value="Widerruf">Widerruf</option>
                <option value="Telefonnummer ungültig">Telefonnummer ungültig</option>
                <option value="Test Lead">Test Lead</option>
              </select>
            </div>
          </div>

          <button style={styles.resetButton} onClick={resetFilters}>
            Filter zurücksetzen
          </button>
        </div>
        
        {error && <div style={styles.error}>{error}</div>}
        {loading ? (
          <div style={styles.loading}>Leads werden geladen...</div>
        ) : leads.length === 0 ? (
          <div style={styles.noData}>
            {Object.values(filters).some(filter => filter !== '' && filter !== 'erstellt' && filter !== 'desc') ? 
              'Keine Leads mit den aktuellen Filtern gefunden.' : 
              'Keine Leads gefunden.'
            }
          </div>
        ) : (
          <div style={styles.tableContainer}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={{...styles.th, ...styles.dateColumn}}>Datum</th>
                  <th style={{...styles.th, ...styles.productColumn}}>Produkt</th>
                  <th style={{...styles.th, ...styles.statusColumn}}>Status</th>
                  <th style={{...styles.th, ...styles.nameColumn}}>Kunde</th>
                  <th style={{...styles.th, ...styles.nameColumn}}>E-Mail</th>
                  <th style={{...styles.th, ...styles.nameColumn}}>Telefon</th>
                  <th style={{...styles.th, ...styles.nameColumn}}>Projekt</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((lead) => (
                  <tr 
                    key={lead.leadId}
                    style={styles.row}
                    onClick={(e) => handleCustomerSelect(lead.id, e)}
                    onMouseEnter={(e) => e.target.parentElement.style.backgroundColor = '#f9fafb'}
                    onMouseLeave={(e) => e.target.parentElement.style.backgroundColor = 'transparent'}
                  >
                    <td style={{...styles.td, ...styles.dateColumn}} title={new Date(lead.erstellt).toLocaleString('de-DE')}>
                      {isMobile ? 
                        new Date(lead.erstellt).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }) :
                        new Date(lead.erstellt).toLocaleDateString('de-DE')
                      }
                    </td>
                    <td style={{...styles.td, ...styles.productColumn}}>
                      <span style={{
                        ...styles.productBadge, 
                        ...(lead.leadType === 'PV' ? styles.pvBadge : styles.wpBadge)
                      }}>
                        {lead.leadType}
                      </span>
                    </td>
                    <td style={{...styles.td, ...styles.statusColumn}}>
                      <span 
                        style={{
                          ...styles.statusBadge, 
                          ...getStatusStyles(lead.status)
                        }} 
                        title={lead.status}
                      >
                        {isMobile && lead.status && lead.status.length > 8 ? 
                          lead.status.substring(0, 8) + '..' : 
                          lead.status || 'Neu'
                        }
                      </span>
                    </td>
                    <td style={{...styles.td, ...styles.nameColumn}} title={`${lead.anrede ? lead.anrede + ' ' : ''}${lead.name || ''} ${lead.nachname || ''}`.trim()}>
                      {truncateText(`${lead.anrede ? lead.anrede + ' ' : ''}${lead.name || ''} ${lead.nachname || ''}`.trim(), isMobile ? 12 : isTablet ? 15 : 20)}
                    </td>
                    <td style={{...styles.td, ...styles.nameColumn}} title={lead.email}>
                      {truncateText(lead.email, isMobile ? 15 : isTablet ? 18 : 25)}
                    </td>
                    <td style={{...styles.td, ...styles.nameColumn}} title={lead.handy}>
                      {truncateText(lead.handy, isMobile ? 12 : 15)}
                    </td>
                    <td style={{...styles.td, ...styles.nameColumn}} title={lead.projekt}>
                      {truncateText(lead.projekt, isMobile ? 8 : isTablet ? 10 : 12)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={styles.mobileScrollHint}>
              ← Horizontal scrollen für weitere Spalten →
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default LeadsOverview;